/* Copyright (c) 2022 MOCA Systems, Inc.  All rights reserved. */

import {
	Component,
	Input,
	ChangeDetectionStrategy,
	Self,
	Optional,
	ChangeDetectorRef,
	OnInit,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject, Observable, combineLatest } from 'rxjs';
import { takeUntil, map, filter, pluck, skipUntil, tap } from 'rxjs/operators';
import { TicketEditBaseComponent } from '../ticket-edit-base/ticket-edit-base.component';
import { MultiWrapperComponent } from '../multi-wrapper/multi-wrapper.component';
import { TicketEditService } from 'src/app/modules/ticket-edit/ticket-edit.service';
import { Location } from '@touchplan/common';
import * as strings from '@touchplan/strings';

interface ILocationLike {
	$id: string;
	view: {
		name?: string;
		color?: string;
	};
}
/**
 * Displays the ticket's location.  If the role is editable, then it's
 * displayed as a dropdown.  If it's not editable, it's displayed
 * in a grayed out panel.
 *
 * Inputs:
 *   list of locations
 *   currently selected location (can be null)
 * Outputs:
 *   what happens on selection change...?
 */
@Component({
	selector: 'ticket-location',
	templateUrl: './ticket-location.component.html',
	host: { class: 'ticket-location--style' },
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketLocationComponent extends TicketEditBaseComponent implements OnInit {
	/**
	 * @input locationList - The list of locations to choose from
	 */
	@Input() public locationList: Subject<Map<string, Location>>;
	@Input() public metaDataKey = 'locationId';
	@Input() public placeholder = strings.TICKET_PLACEHOLDER_LOCATION;

	public locationList$: Observable<Map<string, ILocationLike>>;

	public notSetLabel = strings.GENERAL_NONE;
	public includeNotSetOption = true;

	constructor(
		@Self() @Optional() public control: NgControl,
		@Optional() public multiWrapper: MultiWrapperComponent,
		@Optional() protected cd: ChangeDetectorRef,
		private ticketEditService: TicketEditService
	) {
		super(control, multiWrapper, cd);
	}

	public ngOnInit() {
		super.ngOnInit();
		this.internalField.valueChanges.pipe(takeUntil(this.shutdownSubject)).subscribe((val) => {
			this.onChange(val);
		});

		this.locationList$ = combineLatest([this.locationList, this.ticketEditService.combinedEdit$]).pipe(
			filter(([locations]) => !!locations),
			map(([locations, editTicketsCombined]) => {
				const result = new Map<string, ILocationLike>();

				const isMultiple = editTicketsCombined?.keyMetadata.has(this.metaDataKey);

				// If multi-edit, the not set option should output 'none' as a value
				this.includeNotSetOption = !isMultiple;
				if (isMultiple) {
					result.set(strings.GENERAL_NONE, {
						$id: 'none',
						view: { name: strings.GENERAL_NONE, color: null },
					});
				}
				// Include the rest of the locations
				locations.forEach((location, id: string) => {
					result.set(location.view.name || '', location);
				});
				return result;
			}),
			takeUntil(this.shutdownSubject)
		);
	}

	public alphaSortButWithNoneFirst(a: string, b: string): number {
		if (a === strings.GENERAL_NONE) {
			return -1;
		}
		if (b === strings.GENERAL_NONE) {
			return 1;
		}
		return a.localeCompare(b);
	}
}
